import { useState, useEffect, useCallback } from "react";
import {
    Box,
    Card,
    Typography,
    Grid,
    Stack,
    Chip,
    Avatar,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Pagination,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    alpha,
} from "@mui/material";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { getTransactions, getBalance, getWalletSummary } from "../api";

const formatINR = (v) =>
    v == null || isNaN(Number(v))
        ? "—"
        : `₹${Number(v).toLocaleString("en-IN")}`;

const formatDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt)
        ? "—"
        : dt.toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          });
};

/* Per-gateway commission wallet labels */
const GATEWAY_LABELS = {
    general: "General",
    paytm: "Paytm",
    cashfree: "Cashfree",
    ferry: "Pure Travel",
    slpe: "SLPE",
    superpe: "SuperPe",
};
const gatewayLabel = (g) =>
    GATEWAY_LABELS[String(g || "").toLowerCase()] || g || "Commission";

/* Reference-type chip colours (payin / commission / payout / bbps / refund) */
const REFERENCE_META = {
    payin: { label: "Payin", color: "#0ea5e9" },
    commission: { label: "Commission", color: "#f59e0b" },
    payout: { label: "Payout", color: "#2f8fb5" },
    bbps: { label: "Bill Pay", color: "#14b8a6" },
    refund: { label: "Refund", color: "#64748b" },
};

function ReferenceChip({ type }) {
    if (!type) return <Typography color="text.disabled">—</Typography>;
    const key = String(type).toLowerCase();
    const meta = REFERENCE_META[key] || { label: type, color: "#90a4ae" };
    return (
        <Chip
            size="small"
            label={meta.label}
            sx={{
                fontWeight: 600,
                fontSize: 11,
                color: meta.color,
                bgcolor: alpha(meta.color, 0.12),
            }}
        />
    );
}

export default function Transactions() {
    const [txns, setTxns] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState("ALL");
    const [balance, setBalance] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);

    const fetch = useCallback(() => {
        setLoading(true);
        const params = { page, limit: 20 };
        if (filter !== "ALL") params.type = filter.toLowerCase();
        getTransactions(params)
            .then(({ data }) => {
                setTxns(data.transactions || []);
                setTotal(data.total || 0);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [page, filter]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    useEffect(() => {
        Promise.allSettled([getBalance(), getWalletSummary()]).then(
            ([bal, sum]) => {
                if (bal.status === "fulfilled") setBalance(bal.value.data);
                if (sum.status === "fulfilled") setSummary(sum.value.data);
                setStatsLoading(false);
            },
        );
    }, []);

    const mainBalance = summary?.mainBalance ?? balance?.mainBalance ?? 0;
    const totalCommission =
        summary?.totalCommission ?? balance?.commissionBalance ?? 0;
    const commissionWallets = (summary?.commissionWallets || []).filter(
        (w) => Number(w.balance) > 0,
    );

    const stats = [
        {
            label: "Main Balance",
            value: balance || summary ? formatINR(mainBalance) : "—",
            icon: <AccountBalanceWalletRoundedIcon />,
            color: "#1c6b8c",
        },
        {
            label: "Total Commission",
            value: balance || summary ? formatINR(totalCommission) : "—",
            icon: <SavingsRoundedIcon />,
            color: "#f59e0b",
        },
        {
            label: "Total Credits",
            value: summary
                ? formatINR(summary.totalCredits || summary.todayCredits || 0)
                : "—",
            icon: <ArrowDownwardRoundedIcon />,
            color: "#22c55e",
        },
        {
            label: "Total Debits",
            value: summary
                ? formatINR(summary.totalDebits || summary.todayDebits || 0)
                : "—",
            icon: <ArrowUpwardRoundedIcon />,
            color: "#ef4444",
        },
    ];

    return (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                flexWrap="wrap"
                gap={2}
            >
                <Box>
                    <Typography variant="h5">Wallet Transactions</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Every credit and debit across your wallet
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={filter}
                    onChange={(_, v) => {
                        if (!v) return;
                        setFilter(v);
                        setPage(1);
                    }}
                    sx={{
                        "& .MuiToggleButton-root": {
                            px: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                        },
                    }}
                >
                    <ToggleButton value="ALL">All</ToggleButton>
                    <ToggleButton value="CREDIT">Credits</ToggleButton>
                    <ToggleButton value="DEBIT">Debits</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            <Grid container spacing={2} mb={3}>
                {stats.map((s, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                        <Card sx={{ p: 2, height: "100%" }}>
                            {statsLoading ? (
                                <Skeleton
                                    variant="rectangular"
                                    height={48}
                                    sx={{ borderRadius: 2 }}
                                />
                            ) : (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1.5}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: alpha(s.color, 0.14),
                                            color: s.color,
                                            width: 40,
                                            height: 40,
                                        }}
                                    >
                                        {s.icon}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                        >
                                            {s.label}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight={700}
                                            noWrap
                                        >
                                            {s.value}
                                        </Typography>
                                    </Box>
                                </Stack>
                            )}
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {!statsLoading && commissionWallets.length > 0 && (
                <Card sx={{ p: 2.5, mb: 3 }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                        mb={1.5}
                    >
                        Commission balances by gateway
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                        {commissionWallets.map((w) => (
                            <Chip
                                key={w.walletId || w.gateway}
                                icon={
                                    <SavingsRoundedIcon
                                        sx={{ fontSize: 16 }}
                                    />
                                }
                                label={`${gatewayLabel(w.gateway)} commission ${formatINR(w.balance)}`}
                                sx={{
                                    fontWeight: 600,
                                    color: "#f59e0b",
                                    bgcolor: alpha("#f59e0b", 0.12),
                                    "& .MuiChip-icon": { color: "#f59e0b" },
                                }}
                            />
                        ))}
                    </Stack>
                </Card>
            )}

            <Card sx={{ overflow: "hidden" }}>
                {loading ? (
                    <Box sx={{ p: 3 }}>
                        {[...Array(6)].map((_, i) => (
                            <Skeleton key={i} height={44} sx={{ mb: 0.5 }} />
                        ))}
                    </Box>
                ) : txns.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: "center" }}>
                        <ReceiptLongRoundedIcon
                            sx={{ fontSize: 52, color: "text.disabled", mb: 1 }}
                        />
                        <Typography fontWeight={600} mb={0.5}>
                            {filter === "ALL"
                                ? "No transactions yet"
                                : `No ${filter.toLowerCase()}s found`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {filter === "ALL"
                                ? "Deposits, commissions and bill payments will show up here"
                                : "Try switching the filter above"}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                    <Table sx={{ minWidth: 720 }}>
                        <TableHead>
                            <TableRow
                                sx={{
                                    "& th": {
                                        fontWeight: 700,
                                        fontSize: 12,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.4,
                                        color: "text.secondary",
                                        bgcolor: (t) =>
                                            alpha(t.palette.primary.main, 0.04),
                                    },
                                }}
                            >
                                <TableCell>Transaction</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell align="right">Balance</TableCell>
                                <TableCell>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {txns.map((t) => {
                                const txnType = t.type || t.txnType || "";
                                const isCredit =
                                    String(txnType).toLowerCase() === "credit";
                                const dirColor = isCredit
                                    ? "#22c55e"
                                    : "#ef4444";
                                const balanceAfter =
                                    t.balance_after ?? t.balanceAfter;
                                return (
                                    <TableRow key={t.id} hover>
                                        <TableCell>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                gap={1.5}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 34,
                                                        height: 34,
                                                        color: dirColor,
                                                        bgcolor: alpha(
                                                            dirColor,
                                                            0.12,
                                                        ),
                                                    }}
                                                >
                                                    {isCredit ? (
                                                        <ArrowDownwardRoundedIcon
                                                            sx={{
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                    ) : (
                                                        <ArrowUpwardRoundedIcon
                                                            sx={{
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                    )}
                                                </Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                    >
                                                        {isCredit
                                                            ? "Credit"
                                                            : "Debit"}
                                                    </Typography>
                                                    <Tooltip
                                                        title={
                                                            t.description || ""
                                                        }
                                                        arrow
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                display:
                                                                    "block",
                                                                maxWidth: 260,
                                                            }}
                                                            noWrap
                                                        >
                                                            {t.description ||
                                                                "—"}
                                                        </Typography>
                                                    </Tooltip>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <ReferenceChip
                                                type={
                                                    t.reference_type ||
                                                    t.referenceType
                                                }
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: 700,
                                                whiteSpace: "nowrap",
                                                color: isCredit
                                                    ? "success.main"
                                                    : "error.main",
                                            }}
                                        >
                                            {isCredit ? "+" : "-"}
                                            {formatINR(t.amount || 0)}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: 600,
                                                whiteSpace: "nowrap",
                                                color: "text.secondary",
                                            }}
                                        >
                                            {balanceAfter != null
                                                ? formatINR(balanceAfter)
                                                : "—"}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: "text.secondary",
                                                fontSize: 13,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {formatDate(
                                                t.created_at || t.createdAt,
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    </Box>
                )}
                {Math.ceil(total / 20) > 1 && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 2,
                        }}
                    >
                        <Pagination
                            count={Math.ceil(total / 20)}
                            page={page}
                            onChange={(_, v) => setPage(v)}
                            color="primary"
                        />
                    </Box>
                )}
            </Card>
        </Box>
    );
}
